import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RoomSelection {
    quantity: number
    guestsPerRoom: number
}

export interface GuestDetails {
    title: string
    firstName: string
    lastName: string
    email: string
    phone: string
    country: string
    address: string
    pincode: string
    state: string
}

export interface BookingExtras {
    adults: number
    children: number
    infants: number
    petsAllowed: boolean
    foodAllowed: boolean
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_EXTRAS: BookingExtras = {
    adults: 0, children: 0, infants: 0, petsAllowed: false, foodAllowed: false,
}

const DEFAULT_GUEST: GuestDetails = {
    title: "", firstName: "", lastName: "", email: "", phone: "",
    country: "", address: "", pincode: "", state: "",
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface BookingState {
    rooms: Record<string, RoomSelection>
    dateRange: { from: string; to: string } | null
    extras: BookingExtras
    guestDetails: GuestDetails
}

interface BookingActions {
    setRoom: (id: string, qty: number, guestsPerRoom?: number) => void
    setGuestsPerRoom: (id: string, guests: number) => void
    setDateRange: (range: { from: string; to: string } | null) => void
    setExtras: (extras: Partial<BookingExtras>) => void
    setGuestDetails: (details: Partial<GuestDetails>) => void
    resetBooking: () => void
}

export const useBookingStore = create<BookingState & BookingActions>()(
    persist(
        (set) => ({
            rooms: {},
            dateRange: null,
            extras: DEFAULT_EXTRAS,
            guestDetails: DEFAULT_GUEST,

            setRoom: (id, qty, guestsPerRoom) => set(s => {
                if (qty <= 0) {
                    const { [id]: _, ...rest } = s.rooms
                    return { rooms: rest }
                }
                return {
                    rooms: {
                        ...s.rooms,
                        [id]: {
                            quantity: qty,
                            guestsPerRoom: guestsPerRoom ?? s.rooms[id]?.guestsPerRoom ?? 1,
                        },
                    },
                }
            }),

            setGuestsPerRoom: (id, guests) => set(s => ({
                rooms: { ...s.rooms, [id]: { ...s.rooms[id], guestsPerRoom: guests } },
            })),

            setDateRange: (range) => set({ dateRange: range }),

            setExtras: (extras) => set(s => ({
                extras: { ...s.extras, ...extras },
            })),

            setGuestDetails: (details) => set(s => ({
                guestDetails: { ...s.guestDetails, ...details },
            })),

            resetBooking: () => set({
                rooms: {}, dateRange: null, extras: DEFAULT_EXTRAS, guestDetails: DEFAULT_GUEST,
            }),
        }),
        {
            name: "booking_store",
            storage: createJSONStorage(() => sessionStorage),
        }
    )
)
